import { FlexBox, Spinner } from '@dixa/lib-shared-habla';
import { UpdateUserApiArg, User } from 'lib-shared-types-api';
import { useCallback } from 'react';
import { DeepWritable } from 'ts-essentials';

import { useUpload } from 'apis/uploads';
import { usersApi } from 'apis/users';
// eslint-disable-next-line no-restricted-imports
import AvatarControl from 'components/shared/avatarControl/components/AvatarControl';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { dataUriToFile } from 'utils/file/dataUriToFile';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { profileActions } from '../../../../../../settingsProfile.actions';

import styles from './profileAvatarControl.module.scss';

export const ProfileAvatarControl = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const { user } = useCurrentUser();

  const [upload, { asyncStatus: uploadAsyncStatus }] = useUpload();
  const [updateUser, { isLoading }] = usersApi.useUpdateUserMutation();

  const handleUpdateUser = useCallback(
    async (patch: UpdateUserApiArg['patch']) => {
      try {
        const { data } = await updateUser({
          id: user.id,
          patch,
        }).unwrap();

        dispatch(profileActions.update({ user: data }));

        notify(
          {
            title: 'Success',
            text: `Your avatar has been updated.`,
            icon: 'done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          {
            title: 'Error while updating the avatar.',
          },
          NotificationType.Error,
        );
      }
    },
    [dispatch, notify, updateUser, user.id],
  );

  const handleSelectAvatar = useCallback(
    async (dataURI: string) => {
      const file = await dataUriToFile(dataURI, 'avatar');

      try {
        const { url } = await upload({
          file,
          type: 'user_avatar',
        });

        if (url) {
          await handleUpdateUser({
            updates: {
              avatar_url: url,
            },
          });
        }
      } catch (error) {
        notify(
          {
            title: 'Error while updating the avatar.',
          },
          NotificationType.Error,
        );
      }
    },
    [handleUpdateUser, notify, upload],
  );

  const handleDeleteAvatar = useCallback(() => {
    handleUpdateUser({
      deletes: ['avatar_url'],
    });
  }, [handleUpdateUser]);

  return (
    <FlexBox margin="0 0 400 0" alignItems="center" gap="200">
      <AvatarControl
        className={styles.avatarControl}
        type="user"
        user={user as DeepWritable<User>}
        onSelectAvatar={handleSelectAvatar}
        onDeleteAvatar={handleDeleteAvatar}
      />

      {(uploadAsyncStatus === 'pending' || isLoading) && <Spinner size={20} />}
    </FlexBox>
  );
};
